import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { API_URL, buildKioskAuthHeaders } from './api';
import { getCustomerSessionAccountId, getCustomerOperatorScope } from './kioskCustomerSession';
import './BefidDeviceCustomer.css';

const endpoint = `${API_URL}/api/befid/customer-device`;
const operatorFetch = (url, options) => fetch(url, { ...options, headers: buildKioskAuthHeaders(options.headers) });
const knownOrigins = new Set(['https://beborne.com']);
const deviceCopy = {
  borne: {
    label: 'Connexion BEFID', title: 'Mon compte BEFID',
    description: 'Connecte-toi pour retrouver tes points et tes avantages dans ce restaurant.',
    create: 'Me connecter avec BEFID',
    scan: 'Scanne ce QR code avec ton téléphone pour', confirm: ', puis confirme sur ton téléphone.',
    check: 'J’ai confirmé sur mon téléphone',
    changed: 'La connexion a été interrompue. Recommence depuis cette borne.',
    qrError: 'Le QR code ne peut pas être affiché. Annule puis réessaie.',
    cancelled: 'Demande annulée. Ton panier est conservé.',
    pending: 'Confirme sur ton téléphone, puis appuie sur « J’ai confirmé sur mon téléphone ».',
    cancelUnknown: 'Demande fermée ici. Si l’annulation n’a pas abouti, elle expirera automatiquement. Ton panier est conservé.',
    unavailable: 'Cette demande n’est plus disponible. Annule puis crée un nouveau QR code.',
    unconfirmed: 'Connexion BEFID non confirmée. Réessaie ; ton panier est conservé.',
    deadline: 'Le délai pour scanner ce QR code est dépassé. Ton panier est conservé.',
    expired: 'Cette demande a expiré. Annule puis crée un nouveau QR code.',
  },
  caisse: {
    label: 'Client BEFID', title: 'Client BEFID',
    description: 'Demandez au client de confirmer sur son téléphone pour retrouver ses points et ses avantages dans ce restaurant.',
    create: 'Identifier le client avec BEFID',
    scan: 'Demandez au client de scanner ce QR code avec son téléphone pour', confirm: ', puis de confirmer sur son téléphone.',
    check: 'Vérifier la confirmation du client',
    changed: 'Le restaurant ou le poste a changé. Rouvrez la demande depuis ce poste.',
    qrError: 'Le QR code ne peut pas être affiché. Annulez puis réessayez.',
    cancelled: 'Demande annulée. Le panier est conservé.',
    pending: 'Demandez au client de confirmer sur son téléphone, puis vérifiez sa confirmation.',
    cancelUnknown: 'Demande fermée sur ce poste. Si son annulation n’a pas été reçue, elle expirera automatiquement. Le panier est conservé.',
    unavailable: 'Cette demande n’est plus disponible. Annulez puis créez un nouveau QR code.',
    unconfirmed: 'Connexion BEFID non confirmée. Réessayez ; le panier est conservé.',
    deadline: 'Le délai du QR code est dépassé. Vérifiez la confirmation du client avant de recommencer.',
    expired: 'Cette demande a expiré. Annulez puis créez un nouveau QR code.',
  },
};
export function validDeviceRequest(value, channel) {
  if (!value || !/^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(value.requestId || '') || !/^[a-f0-9]{48}$/.test(value.code || '')
    || !/^[a-f0-9]{64}$/.test(value.browserSecret || '') || value.channel !== channel
    || typeof value.slug !== 'string' || !/^[A-Za-z0-9_-]{1,120}$/.test(value.slug)
    || typeof value.destinationName !== 'string' || !value.destinationName.trim() || value.destinationName.length > 200
    || !Number.isFinite(Date.parse(value.expiresAt))) return false;
  try {
    const url = new URL(value.approvalUrl);
    return knownOrigins.has(url.origin) && !url.username && !url.password && !url.search
      && url.pathname === `/identite/appareil/${value.slug}` && url.hash === `#code=${value.code}`;
  } catch { return false; }
}
function validCustomer(value, accountId) {
  return value && Number.isSafeInteger(value.id) && value.id > 0 && String(value.accountId) === accountId
    && typeof value.firstName === 'string' && typeof value.phone === 'string'
    && Number.isFinite(value.points) && Number.isFinite(value.availablePoints);
}

// Only the local customer profile leaves this component. Pairing credentials never leave memory.
export default function BefidDeviceCustomer({ channel, accountId = getCustomerSessionAccountId(), fetcher = operatorFetch, onCustomer, onPendingChange, disabled = false }) {
  const copy = channel === 'borne' ? deviceCopy.borne : deviceCopy.caisse;
  const [request, setRequest] = useState(null);
  const [qr, setQr] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const [expired, setExpired] = useState(false);
  const active = useRef(false);
  const operator = useRef(getCustomerOperatorScope());
  const controllers = useRef(new Set());
  const generation = useRef(0);
  const running = useRef(false);
  const requestRef = useRef(null);
  const fetchRef = useRef(fetcher);
  fetchRef.current = fetcher;
  const disabledRef = useRef(disabled); disabledRef.current = disabled;
  const customerCallback = useRef(onCustomer); customerCallback.current = onCustomer;
  const pendingCallback = useRef(onPendingChange); pendingCallback.current = onPendingChange;
  useEffect(() => {
    pendingCallback.current?.(!!request && !expired && !['expired', 'cancelled'].includes(status));
  }, [request, expired, status]);
  const sameOperator = () => accountId && accountId === getCustomerSessionAccountId() && operator.current === getCustomerOperatorScope();
  const current = () => active.current && sameOperator();
  const boundedRequest = async (url, options) => {
    const controller = new AbortController(); controllers.current.add(controller);
    let timeout;
    try {
      return await Promise.race([
        Promise.resolve().then(async () => {
          const response = await fetchRef.current(url, { ...options, signal: controller.signal });
          return { response, payload: await response.json() };
        }),
        new Promise((_, reject) => { timeout = setTimeout(() => { controller.abort(); reject(new Error('TIMEOUT')); }, 10000); }),
      ]);
    } finally { clearTimeout(timeout); controllers.current.delete(controller); }
  };
  const cancelRequest = (value) => {
    if (!value || !sameOperator()) return Promise.resolve();
    return boundedRequest(`${endpoint}/${value.requestId}/cancel`, {
      method: 'POST', credentials: 'omit', cache: 'no-store',
      headers: { 'Content-Type': 'application/json', 'X-BE-Pairing-Secret': value.browserSecret }, body: '{}',
    }).catch(() => {});
  };
  useEffect(() => {
    active.current = true; operator.current = getCustomerOperatorScope();
    const requestControllers = controllers.current;
    setRequest(null); setQr(''); setStatus(''); setBusy(false); running.current = false;
    const reset = () => {
      if (!sameOperator()) {
        generation.current += 1; requestRef.current = null; setRequest(null); setQr(''); setError(copy.changed);
      }
    };
    window.addEventListener('storage', reset); window.addEventListener('focus', reset);
    return () => {
      active.current = false; generation.current += 1;
      pendingCallback.current?.(false);
      requestControllers.forEach((controller) => controller.abort());
      cancelRequest(requestRef.current); requestRef.current = null;
      window.removeEventListener('storage', reset); window.removeEventListener('focus', reset);
    };
    // The host keys this component by restaurant and ticket generation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, channel]);
  useEffect(() => {
    if (!request) return undefined;
    let live = true;
    QRCode.toDataURL(request.approvalUrl, { width: 256, margin: 2, errorCorrectionLevel: 'M' })
      .then((value) => { if (live) setQr(value); }).catch(() => { if (live) setError(copy.qrError); });
    const tick = () => setExpired(Date.now() >= request.localDeadline);
    tick(); const timer = setInterval(tick, 1000);
    return () => { live = false; clearInterval(timer); };
  }, [request, copy]);
  const perform = async (action) => {
    if (running.current || (disabled && action !== 'cancel') || !current()) return;
    const scope = generation.current;
    const stillCurrent = () => current() && generation.current === scope && (action === 'cancel' || !disabledRef.current);
    running.current = true; setBusy(true); setError(''); setMessage('');
    const call = async (path, method, body, value = requestRef.current) => {
      if (!stillCurrent()) throw new Error('STALE');
      const { response, payload } = await boundedRequest(`${endpoint}${path}`, {
        method, credentials: 'omit', cache: 'no-store',
        headers: { ...(method === 'POST' ? { 'Content-Type': 'application/json' } : {}), ...(value ? { 'X-BE-Pairing-Secret': value.browserSecret } : {}) },
        ...(method === 'POST' ? { body: JSON.stringify(body) } : {}),
      });
      if (!response.ok) throw Object.assign(new Error('REFUSED'), { code: payload?.code });
      return payload;
    };
    try {
      if (action === 'create') {
        const value = await call('', 'POST', { channel }, null);
        if (!validDeviceRequest(value, channel)) throw new Error('INVALID');
        if (!stillCurrent()) { await cancelRequest(value); return; }
        const pending = { ...value, localDeadline: Math.min(Date.parse(value.expiresAt), Date.now() + 300000) };
        requestRef.current = pending; setRequest(pending); setStatus('pending');
      } else if (action === 'cancel') {
        await call(`/${requestRef.current.requestId}/cancel`, 'POST', {});
        if (stillCurrent()) { requestRef.current = null; setRequest(null); setQr(''); setStatus(''); setMessage(copy.cancelled); }
      } else {
        const value = requestRef.current;
        const result = await call(`/${value.requestId}`, 'GET');
        if (!stillCurrent()) return;
        if (!['pending', 'approved', 'consumed', 'expired', 'cancelled'].includes(result?.status)) throw new Error('INVALID');
        setStatus(result.status);
        if (result.status === 'approved' || result.status === 'consumed') {
          const receipt = await call(`/${value.requestId}/consume`, 'POST', {});
          if (!stillCurrent()) return;
          if (!validCustomer(receipt?.customer, accountId)) throw new Error('INVALID');
          const { id, accountId: tenant, firstName, phone, points, availablePoints } = receipt.customer;
          requestRef.current = null; setRequest(null); setQr(''); setStatus('');
          customerCallback.current({ id, accountId: tenant, firstName, phone, points, availablePoints });
        } else if (result.status === 'pending') setMessage(copy.pending);
      }
    } catch (failure) {
      if (stillCurrent() && action === 'cancel') {
        requestRef.current = null; setRequest(null); setQr(''); setStatus('');
        setMessage(copy.cancelUnknown);
        return;
      }
      if (stillCurrent()) setError(failure.code === 'BEFID_IDENTITY_DEVICE_INVALID'
        ? copy.unavailable
        : copy.unconfirmed);
    } finally {
      if (generation.current === scope) {
        running.current = false;
        if (active.current) setBusy(false);
      }
    }
  };
  return <section className="befid-device-customer" aria-label={copy.label}>
    <strong>{copy.title}</strong>
    <p>{copy.description}</p>
    {!request ? <button type="button" disabled={disabled || busy || !accountId} onClick={() => perform('create')}>{busy ? 'Préparation…' : copy.create}</button> : <>
      <p>{copy.scan} <strong>{request.destinationName}</strong>{copy.confirm}</p>
      {qr && !expired && !['cancelled', 'expired'].includes(status) && <img src={qr} width="256" height="256" alt="QR code de confirmation BEFID" />}
      {expired && <p>{copy.deadline}</p>}
      {status === 'cancelled' && <p>Cette demande a été annulée.</p>}
      {status === 'expired' && <p>{copy.expired}</p>}
      <div className="befid-device-actions">
        <button type="button" disabled={disabled || busy || ['expired', 'cancelled'].includes(status)} onClick={() => perform('check')}>{copy.check}</button>
        <button type="button" disabled={busy} onClick={() => perform('cancel')}>Annuler la demande</button>
      </div>
    </>}
    {message && <p role="status">{message}</p>}{error && <p role="alert">{error}</p>}
  </section>;
}
